import apiClient from './api';
import {
  CatalogueCategory,
  GetAllCatalogueCategoriesResponse,
  CreateCatalogueCategoryRequest,
  CreateCatalogueCategoryResponse,
  UpdateCatalogueCategoryRequest,
  UpdateCatalogueCategoryResponse,
  DeleteCatalogueCategoryResponse,
  ReorderCatalogueCategoriesRequest,
  ReorderCatalogueCategoriesResponse,
  GetAllCatalogueProfilesResponse,
  GetAllCatalogueServicesResponse,
  GetAllCatalogueQuestionsResponse,
  ApproveItemResponse,
  SuspendItemResponse,
  ApproveProfileResponse,
  SuspendProfileResponse,
  CatalogueProfile,
  CatalogueService,
  CatalogueItemActionResponse,
  APICatalogueProfile,
  APICatalogueService,
  CatalogueItemVersionID,
  CatalogueServiceBulkApproveRequest,
  CatalogueBulkApproveResponse,
  BulkApproveProfilesResponse,
  ApproveServiceResponse,
  SuspendServiceResponse,
  BulkApproveServicesResponse,
} from '../types/catalogue.types';
import { APIPaginatedResponse, MessageInfo } from '../types';
import { buildErrorResponse } from '../utils/api';
import { formatEnAr, formatProductLocaleField } from '../utils/bilingualDisplay';

const mapAPIProfile = ({ details, versionId }: APICatalogueProfile): CatalogueProfile => ({
  vendorId: details.vendorId,
  versionId,
  businessName: details.companyName ?? '',
  category: details.businessType,
  tags: details.categoryTree?.items?.join(', ') ?? '',
  imageURL: details.profileImageUrl,
})

const mapAPIService = ({ details, versionId }: APICatalogueService): CatalogueService => {
  const firstPkg = details.packages?.[0];
  return {
    productId: details.productId,
    versionId,
    vendorId: details.vendorId,
    vendorName: details.vendorName,
    title: formatProductLocaleField(details.title, details.locale, 'title'),
    price: details.amount,
    description: formatProductLocaleField(details.shortDescription, details.locale, 'shortDescription'),
    imageURL: details.imageURLs?.[0],
    packageName: firstPkg
      ? formatEnAr(
          (firstPkg.locale?.en?.title ?? firstPkg.title).trim(),
          (firstPkg.locale?.ar?.title ?? '').trim(),
        )
      : undefined,
  };
};

export const catalogueService = {
  // ==================== CATALOGUE CATEGORIES ====================

  /**
   * Get all catalogue categories
   */
  getAllCategories: async (): Promise<GetAllCatalogueCategoriesResponse> => {
    const response = await apiClient.get<GetAllCatalogueCategoriesResponse>(
      '/categories-catalogue'
    );
    return response.data;
  },

  /**
   * Get catalogue category by ID
   */
  getCategoryById: async (categoryId: string): Promise<{ data: CatalogueCategory }> => {
    const response = await apiClient.get<{ data: CatalogueCategory }>(
      `/categories-catalogue/${categoryId}`
    );
    return response.data;
  },

  /**
   * Create new catalogue category
   */
  createCategory: async (
    data: CreateCatalogueCategoryRequest
  ): Promise<CreateCatalogueCategoryResponse> => {
    const response = await apiClient.post<CreateCatalogueCategoryResponse>(
      '/categories-catalogue',
      data
    );
    return response.data;
  },

  /**
   * Update catalogue category
   */
  updateCategory: async (
    categoryId: string,
    data: UpdateCatalogueCategoryRequest
  ): Promise<UpdateCatalogueCategoryResponse> => {
    const response = await apiClient.patch<UpdateCatalogueCategoryResponse>(
      `/categories-catalogue/${categoryId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete catalogue category
   */
  deleteCategory: async (categoryId: string): Promise<DeleteCatalogueCategoryResponse> => {
    const response = await apiClient.delete<DeleteCatalogueCategoryResponse>(
      `/categories-catalogue/${categoryId}`
    );
    return response.data;
  },

  /**
   * Reorder catalogue categories
   */
  reorderCategories: async (
    data: ReorderCatalogueCategoriesRequest
  ): Promise<ReorderCatalogueCategoriesResponse> => {
    const response = await apiClient.patch<ReorderCatalogueCategoriesResponse>(
      '/categories-catalogue/reorder',
      data
    );
    return response.data;
  },

  // ==================== CATALOGUE PROFILES (VENDORS) ====================

  /**
   * Get all catalogue profiles (vendors pending approval)
   */
  getAllProfiles: async (limit: number, afterCursor?: string): Promise<APIPaginatedResponse<CatalogueProfile>> => {
    try {
      const response = await apiClient.get<GetAllCatalogueProfilesResponse>(
        '/vendors/in-review',
        {
          params: {
            afterCursor,
            limit,
          }
        }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: {
          items: data.items.map(mapAPIProfile),
          endCursor: data.endCursor
        }
      };
    } catch (error) {
      return buildErrorResponse('Error fetching profiles', error);
    }
  },

  /**
   * Approve catalogue profile (vendor)
   */
  approveProfile: async (profileId: string, versionId: CatalogueItemVersionID): Promise<CatalogueItemActionResponse<CatalogueProfile>> => {
    try {
      const response = await apiClient.post<ApproveProfileResponse>(
        `/vendors/${profileId}/approve`,
        { versionId }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: mapAPIProfile(data),
        messageInfo: {
          message: 'Profile approved successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error approving profile', error);
    }
  },

  /**
   * Suspend catalogue profile (vendor)
   */
  suspendProfile: async (profileId: string, versionId: CatalogueItemVersionID): Promise<CatalogueItemActionResponse<CatalogueProfile>> => {
    try {
      const response = await apiClient.post<SuspendProfileResponse>(
        `/vendors/${profileId}/suspend`,
        { versionId }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: mapAPIProfile(data),
        messageInfo: {
          message: 'Profile suspended successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error suspending profile', error);
    }
  },

  /**
   * Bulk approve all catalogue profiles (vendors)
   */
  bulkApproveProfiles: async (vendors: CatalogueServiceBulkApproveRequest<'vendorId', string>): Promise<CatalogueBulkApproveResponse<'vendorId', string>> => {
    try {
      const response = await apiClient.post<BulkApproveProfilesResponse>(
        '/admins/vendors/bulk-approve',
        { vendors }
      );

      const data = response.data.data;

      const results = data.results
      const totalCount = results.length;
      const successCount = results.filter(item => item.success).length;
      const failedCount = totalCount - successCount;

      let messageInfo: MessageInfo;

      if (failedCount === 0) {
        messageInfo = {
          message: `Successfully approved all ${successCount} profile(s)!`,
        }
      } else if (successCount === 0) {
        messageInfo = {
          error: `Failed to approve all ${failedCount} profile(s)`,
        }
      } else {
        messageInfo = {
          warning: `Approved ${successCount} of ${totalCount} profile(s). ${failedCount} failed.`,
        }
      }

      return {
        ok: true,
        data: {
          results,
        },
        messageInfo
      }
    } catch (error) {
      return buildErrorResponse('Error approving profiles', error);
    }
  },

  /**
   * Get vendor profile by ID (Admin endpoint)
   */
  getProfileById: async (vendorId: string): Promise<any> => {
    const response = await apiClient.get(`/vendors/${vendorId}`);
    return response.data;
  },

  // ==================== CATALOGUE SERVICES (PRODUCTS) ====================

  /**
   * Get all catalogue services (products pending approval)
   */
  getAllServices: async (limit: number, afterCursor?: string): Promise<APIPaginatedResponse<CatalogueService>> => {
    try {
      const response = await apiClient.get<GetAllCatalogueServicesResponse>(
        '/products?status=InReview',
        {
          params: {
            afterCursor,
            limit,
          }
        }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: {
          items: data.items.map(mapAPIService),
          endCursor: data.endCursor
        }
      };
    } catch (error) {
      return buildErrorResponse('Error fetching services', error);
    }
  },

  /**
   * Approve catalogue service (product)
   */
  approveService: async (serviceId: string, versionId: CatalogueItemVersionID): Promise<CatalogueItemActionResponse<CatalogueService>> => {
    try {
      const response = await apiClient.post<ApproveServiceResponse>(
        `/products/${serviceId}/status`,
        { versionId, status: 'Approved' }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: mapAPIService(data),
        messageInfo: {
          message: 'Service approved successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error approving service', error);
    }
  },

  /**
   * Suspend catalogue service (product)
   */
  suspendService: async (serviceId: string, versionId: CatalogueItemVersionID, reason?: string): Promise<CatalogueItemActionResponse<CatalogueService>> => {
    try {
      const response = await apiClient.post<SuspendServiceResponse>(
        `/products/${serviceId}/status`,
        { versionId, status: 'Suspended', reason }
      );

      const data = response.data.data;

      return {
        ok: true,
        data: mapAPIService(data),
        messageInfo: {
          message: 'Service suspended successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error suspending service', error);
    }
  },

  /**
   * Bulk approve all catalogue services (products)
   */
  bulkApproveServices: async (products: CatalogueServiceBulkApproveRequest<'productId', string>): Promise<CatalogueBulkApproveResponse<'productId', string>> => {
    try {
      const response = await apiClient.post<BulkApproveServicesResponse>(
        '/admins/products/bulk-approve',
        { products }
      );

      const data = response.data.data;

      const results = data.results
      const totalCount = results.length;
      const successCount = results.filter(item => item.success).length;
      const failedCount = totalCount - successCount;

      let messageInfo: MessageInfo;

      if (failedCount === 0) {
        messageInfo = {
          message: `Successfully approved all ${successCount} service(s)!`,
        }
      } else if (successCount === 0) {
        messageInfo = {
          error: `Failed to approve all ${failedCount} service(s)`,
        }
      } else {
        messageInfo = {
          warning: `Approved ${successCount} of ${totalCount} service(s). ${failedCount} failed.`,
        }
      }

      return {
        ok: true,
        data: {
          results,
        },
        messageInfo
      }
    } catch (error) {
      return buildErrorResponse('Error approving services', error);
    }
  },

  /**
   * Get service (product) by ID
   */
  getServiceById: async (productId: string): Promise<any> => {
    const response = await apiClient.get(`/admins/products/${productId}`);
    return response.data;
  },

  // ==================== CATALOGUE QUESTIONS ====================

  /**
   * Get all catalogue questions (pending answer)
   */
  getAllQuestions: async (): Promise<GetAllCatalogueQuestionsResponse> => {
    const response = await apiClient.get<GetAllCatalogueQuestionsResponse>(
      '/questions?status=pending'
    );
    return response.data;
  },

  /**
   * Approve question
   */
  approveQuestion: async (questionId: string): Promise<ApproveItemResponse> => {
    const response = await apiClient.post<ApproveItemResponse>(
      `/questions/${questionId}/approve`
    );
    return response.data;
  },

  /**
   * Decline question
   */
  declineQuestion: async (questionId: string, reason?: string): Promise<SuspendItemResponse> => {
    const response = await apiClient.post<SuspendItemResponse>(
      `/questions/${questionId}/decline`,
      { reason }
    );
    return response.data;
  },
};

